import React from 'react';
import * as XLSX from 'xlsx';

const ExecutiveDatabase = ({
  sheetData,
  sheetLoading,
  filteredData,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  searchTerm,
  setSearchTerm,
  handleRowClick,
  fetchSheetData
}) => {
  // Export to Excel function
  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert filtered data to worksheet
    const ws = XLSX.utils.json_to_sheet(filteredData);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Executive Data');
    
    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0];
    const filename = `executive_data_${date}.xlsx`;
    
    // Save the file
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl p-8 animate-slide-up backdrop-blur-sm">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <h2 className="text-3xl font-bold text-yellow-400 flex items-center">
          Executive Database
        </h2>
        <div className="flex flex-row gap-3 w-full sm:w-auto justify-center">
          <button
            onClick={exportToExcel}
            disabled={!filteredData || filteredData.length === 0}
            className={`group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform overflow-hidden ${
              !filteredData || filteredData.length === 0
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-[#1E5631] text-[#EAEAEA] hover:bg-[#1A4E2B] hover:scale-105 hover:shadow-lg hover:shadow-[#1E5631]/30 active:scale-95'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <span className="relative z-10 flex items-center gap-2">
               Export
            </span>
          </button>
          <button
            onClick={fetchSheetData}
            disabled={sheetLoading}
            className={`group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform overflow-hidden ${
              sheetLoading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-[#B8860B] text-[#EAEAEA] hover:bg-[#A6750A] hover:scale-105 active:scale-95'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <span className="relative z-10">
              {sheetLoading ? 'Syncing...' : 'Refresh Data'}
            </span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {sheetData.length > 0 && (
        <div className="mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search executives, companies, positions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-6 py-4 pl-14 bg-black/50 border-2 border-yellow-500/30 rounded-2xl text-white placeholder-gray-400 focus:ring-4 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all duration-300 text-lg backdrop-blur-sm"
            />
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-yellow-500 text-xl">
              🔍
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-yellow-400 transition-colors duration-200 text-xl"
              >
                ✕
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-yellow-300 mt-3 text-sm">
              Found {filteredData.length} executive{filteredData.length !== 1 ? 's' : ''} matching "{searchTerm}"
            </p>
          )}
        </div>
      )}

      {sheetLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-3 border-yellow-500 border-t-transparent rounded-full animate-spin mr-4"></div>
          <span className="text-yellow-300 text-lg">Accessing executive database...</span>
        </div>
      ) : filteredData.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-2xl border border-yellow-500/20">
            <table className="min-w-full border-collapse bg-black/30 backdrop-blur-sm">
              <thead className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20">
                <tr>
                  {Object.keys(filteredData[0]).map((header, index) => (
                    <th key={index} className="border-r border-yellow-500/20 px-6 py-4 text-left font-bold text-yellow-400 whitespace-nowrap min-w-[140px] uppercase tracking-wide text-sm">
                      {header.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData
                  .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                  .map((row, index) => (
                  <tr
                  key={index}
                  onClick={() => handleRowClick(row)}
                  className={`${
                      ((currentPage - 1) * rowsPerPage + index) % 2 === 0
                      ? 'bg-black/20'
                      : 'bg-gray-900/30'
                  } hover:bg-gradient-to-r hover:from-yellow-500/10 hover:to-yellow-600/10 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-yellow-500/10 group`}
                  >
                  {Object.values(row).map((cell, cellIndex) => (
                      <td key={cellIndex} className="border-r border-yellow-500/10 px-6 py-4 text-gray-200 text-sm whitespace-nowrap group-hover:text-white transition-colors duration-200">
                      {/* If this is the emails column, show only first 1-2 emails and ellipsis if more */}
                      {Object.keys(filteredData[0])[cellIndex] === 'emails' && cell ? (
                        (() => {
                          const emailsArr = cell.split(',').map(e => e.trim()).filter(Boolean);
                          if (emailsArr.length === 0) return '';
                          if (emailsArr.length <= 2) return emailsArr.join(', ');
                          return emailsArr.slice(0, 2).join(', ') + ' ...';
                        })()
                      ) : cell}
                      </td>
                  ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {filteredData.length > rowsPerPage && (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-8 pt-6 border-t border-yellow-500/20 gap-4">
              <div className="text-gray-300 text-center sm:text-left text-sm">
                Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} executives
                {searchTerm && <span className="text-yellow-400 ml-2">(filtered)</span>}
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2 flex-wrap justify-center">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    currentPage === 1 
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 text-yellow-300 hover:from-yellow-500/30 hover:to-yellow-600/30 hover:text-yellow-200'
                  }`}
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">←</span>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.ceil(filteredData.length / rowsPerPage) }, (_, i) => i + 1)
                  .filter(page => {
                    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
                    // On mobile, show fewer pages
                    const isMobile = window.innerWidth < 640;
                    if (isMobile) {
                      return page === 1 || 
                             page === totalPages || 
                             page === currentPage;
                    }
                    return page === 1 || 
                           page === totalPages || 
                           (page >= currentPage - 1 && page <= currentPage + 1);
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-1 sm:px-2 text-gray-500 text-xs sm:text-sm">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 min-w-[36px] sm:min-w-[40px] ${
                          currentPage === page
                            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black shadow-lg shadow-yellow-500/30'
                            : 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 text-yellow-300 hover:from-yellow-500/30 hover:to-yellow-600/30 hover:text-yellow-200'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
                
                <button
                  onClick={() => setCurrentPage(Math.min(Math.ceil(filteredData.length / rowsPerPage), currentPage + 1))}
                  disabled={currentPage === Math.ceil(filteredData.length / rowsPerPage)}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    currentPage === Math.ceil(filteredData.length / rowsPerPage)
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 text-yellow-300 hover:from-yellow-500/30 hover:to-yellow-600/30 hover:text-yellow-200'
                  }`}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">→</span>
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16">
          <div className="text-yellow-500 text-4xl mb-4">📋</div>
          <p className="text-gray-300 text-xl mb-2">No executive data available</p>
          <p className="text-gray-500">
            Ensure your executive database is properly configured
          </p>
        </div>
      )}
    </div>
  );
};

export default ExecutiveDatabase;